import { GET_USER_REGISTRIES } from '~/types/actions';
import { formatDateMcom, formatDateBcom } from '~/utils/formatDate';

import { useEnvPropsStore } from '~/stores/envProps';
import { useLocalsStore } from '~/stores/locals';
import { useBaseStore } from '~/stores/base';

export const useAssociation = () => {
  const envPropsStore = useEnvPropsStore();
  const localsStore = useLocalsStore();

  const registryId = ref('');
  const registryGUID = ref('');
  const registryStatus = ref('');
  const occasionType = ref('');

  const getCookieValue = (name) => useCookie(name)?.value;

  const orgId = computed(() => (envPropsStore.envProps.entryPoint?.toUpperCase() === 'MCOM' ? 12 : 13));
  const userId = computed(() => {
    if (orgId.value === 13) {
      return getCookieValue('bloomingdales_online_uid');
    }
    return getCookieValue('macys_online_uid');
  });

  const userGuid = computed(() => {
    if (orgId.value === 13) {
      return getCookieValue('bloomingdales_online_guid');
    }
    return getCookieValue('macys_online_guid');
  });

  const isSignedIn = computed(() => !!Number(getCookieValue('SignedIn')));
  const registriesAllowedConfig = computed(() => localsStore.data.pageData.registryConfig);

  const setRegistryDetails = () => {
    try {
      const userRegistries = JSON.parse(window.localStorage.getItem('userRegistries'));
      registryId.value = userRegistries?.registries?.find((item) => !!item.defaultRegistry)?.registryId;
      registryGUID.value = userRegistries?.registries?.find((item) => !!item.defaultRegistry)?.registryGUID;
      registryStatus.value = userRegistries?.registries?.find((item) => !!item.defaultRegistry)?.registryStatus;
      occasionType.value = userRegistries?.registries?.find((item) => !!item.defaultRegistry)?.occasionType?.toUpperCase();
    } catch {
      registryId.value = '';
      registryGUID.value = '';
      registryStatus.value = '';
      occasionType.value = '';
    }
  };

  if (import.meta.client) {
    setRegistryDetails();
  }

  const getUserRegistries = async (fromLanding = false) => {
    const baseStore = useBaseStore();
    if (isSignedIn.value) {
      await baseStore[GET_USER_REGISTRIES]({
        userId: userId.value,
        userGUID: userGuid.value,
        orgId: orgId.value,
        fromLanding,
      });
    }
    setRegistryDetails();
  };

  const noOfRegistriesAllowed = (occasion) => {
    try {
      const config = registriesAllowedConfig.value.find((item) => item.registryConfigSubType.toUpperCase() === occasion.toUpperCase());
      return Number(config.registryConfigValue);
    } catch {
      return 0;
    }
  };

  const isNoOfRegistriesExceeded = (occasion) => {
    try {
      const userRegistries = JSON.parse(window.localStorage.getItem('userRegistries'));
      const registries = userRegistries.registries.filter((item) => item.occasionType.toUpperCase() === occasion.toUpperCase());
      return registries.length >= noOfRegistriesAllowed(occasion);
    } catch {
      return true;
    }
  };

  const isOccasionAllowed = (occasion) => {
    try {
      const userRegistries = JSON.parse(window.localStorage.getItem('userRegistries'));
      return userRegistries.allowedOccasions.toUpperCase().includes(occasion.toUpperCase());
    } catch {
      return false;
    }
  };

  const getDefaultRegistry = (occasion) => {
    let registry = { registryGUID: '' };
    try {
      if (occasion && (isNoOfRegistriesExceeded(occasion) || !isOccasionAllowed(occasion))) {
        const userRegistries = JSON.parse(window.localStorage.getItem('userRegistries'));
        const status = ['COMPLETION', 'ACTIVE', 'DRAFT', ''];
        let registryFound = false;
        status.every((stat) => {
          userRegistries.registries.every((reg) => {
            if (reg.occasionType.toUpperCase() === occasion.toUpperCase()) {
              if (reg.registryStatus.toUpperCase() === stat || !stat) {
                registry = reg;
                registryFound = true;
                return false;
              }
            }
            return true;
          });
          if (registryFound) {
            return false;
          }
          return true;
        });
      }
      return registry;
    } catch {
      return registry;
    }
  };

  const getRegistriesByOccasion = (occasion) => {
    try {
      const { registries } = JSON.parse(window.localStorage.getItem('userRegistries'));
      const filteredRegistries = registries.filter((item) => item.occasionType.toUpperCase() === occasion.toUpperCase());
      return filteredRegistries.map((item) => {
        const formattedOccasionDate = orgId.value === 12 ? formatDateMcom(item.occasionDate) : formatDateBcom(item.occasionDate);
        return {
          ...item,
          formattedOccasionDate,
          name: filteredRegistries.length === 1 ? 'Go Now' : formattedOccasionDate,
        };
      });
    } catch {
      return [];
    }
  };

  const isManageRegistryExceededForLanding = (occasion) => {
    try {
      if (occasion === 'wedding' || (occasion === 'baby' && (isNoOfRegistriesExceeded(occasion) || !isOccasionAllowed(occasion)))) {
        return true;
      }
    } catch {
      return false;
    }
    return false;
  };

  return {
    registryId,
    registryGUID,
    registryStatus,
    occasionType,
    orgId,
    userId,
    userGuid,
    getUserRegistries,
    setRegistryDetails,
    isSignedIn,
    registriesAllowedConfig,
    noOfRegistriesAllowed,
    isNoOfRegistriesExceeded,
    isOccasionAllowed,
    getDefaultRegistry,
    formatDateMcom,
    formatDateBcom,
    getRegistriesByOccasion,
    isManageRegistryExceededForLanding,
  };
};
